import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import Card from "../../components/Card";
import { POST_PLATFORMS } from "../../lib/helpers";
import { useConfirm } from "../../components/ConfirmDialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

// L'annuaire des tiers qu'on cite dans les publications (décision de Mélilia du 27/10).
//
// C'est le niveau PARTAGÉ : une marque y est écrite une fois, et toutes les entreprises qui la
// taguent suivent. Corriger un pseudo ici le corrige partout — c'est toute la raison d'être de
// l'annuaire. Ce qui est propre à UNE entreprise (son commercial de secteur) ne s'écrit pas ici
// mais sur le lien, dans la page de son module Social Media.
//
// L'entreprise ne voit JAMAIS cet écran : elle ne voit que les marques qu'on lui a rattachées.

const KINDS = ["Entité tierce", "Marque citée"];
const vide = { name: "", kind: "Entité tierce" };

export default function AdminEntities() {
  const { confirm } = useConfirm();
  const [entites, setEntites] = useState([]);
  const [recherche, setRecherche] = useState("");
  const [creation, setCreation] = useState(null);      // null = fermé
  const [ouvert, setOuvert] = useState(null);
  const [compte, setCompte] = useState({ network: "Instagram", handle: "", name: "" });
  const [aVerifier, setAVerifier] = useState(false);

  const charger = useCallback(() => api.get("/admin/entities").then((r) => setEntites(r.data)), []);
  useEffect(() => { charger().catch(() => toast.error("Chargement impossible")); }, [charger]);

  const filtrees = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    let out = entites;
    if (q) out = out.filter((e) => [e.name, e.kind, ...(e.accounts || []).map((c) => c.handle)]
      .join(" ").toLowerCase().includes(q));
    // « À vérifier » retient aussi les centres d'intérêt SANS aucun compte : eux non plus n'ont
    // rien de confirmé, et ce sont les plus faciles à oublier.
    if (aVerifier) out = out.filter((e) => !(e.accounts || []).length || (e.accounts || []).some((c) => !c.verified));
    return out;
  }, [entites, recherche, aVerifier]);

  const sansCompte = entites.filter((e) => !(e.accounts || []).length).length;
  const nonVerifies = entites.reduce((n, e) => n + (e.accounts || []).filter((c) => !c.verified).length, 0);

  const creer = async () => {
    if (!creation.name.trim()) return toast.error("Donnez un nom à la marque");
    try { await api.post("/admin/entities", { ...creation, name: creation.name.trim(), accounts: [] }); setCreation(null); charger(); toast.success("Marque ajoutée à l'annuaire"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
  };
  const ecrire = async (e, champs, message) => {
    try { await api.put(`/admin/entities/${e.id}`, champs); charger(); if (message) toast.success(message); }
    catch (err) { toast.error(err?.response?.data?.detail || "Enregistrement impossible"); }
  };
  const supprimer = async (e) => {
    if (!(await confirm({ title: `Retirer ${e.name} de l'annuaire ?`, message: "Elle disparaîtra pour toutes les entreprises. Cette action ne se défait pas.", tone: "danger" }))) return;
    try { await api.delete(`/admin/entities/${e.id}`); charger(); toast.success("Marque retirée"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Suppression impossible"); }
  };
  const ajouterCompte = async (e) => {
    if (!compte.handle.trim()) return toast.error("Indiquez l'identifiant du compte");
    await ecrire(e, { accounts: [...(e.accounts || []), { ...compte, handle: compte.handle.trim(), name: compte.name.trim(), verified: true }] }, "Compte ajouté");
    setCompte({ network: "Instagram", handle: "", name: "" });
  };
  const retirerCompte = (e, i) => ecrire(e, { accounts: (e.accounts || []).filter((_, x) => x !== i) }, "Compte retiré");
  // Confirmer l'existence d'un compte à la main. La vérification automatique viendra ; en
  // attendant l'état existe, se voit et se filtre — c'est lui qui compte.
  const basculerVerifie = (e, i) => ecrire(e, { accounts: (e.accounts || []).map((c, x) => x === i ? { ...c, verified: !c.verified } : c) },
    (e.accounts[i].verified ? "Repassé à vérifier" : "Compte vérifié"));

  return (
    <div data-testid="admin-entities">
      <PageTitle overline="Clients" title="Entités"
        action={<button onClick={() => setCreation(creation ? null : { ...vide })} data-testid="entite-nouvelle"
          className="bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover transition-colors flex items-center gap-1.5">
          <Icons.Plus size={16} /> Nouvelle marque</button>} />

      <Card>
        <p className="text-sm text-marine/80 mb-4">
          Les tiers qu'on mentionne dans les publications : laboratoires, fabricants, sociétés savantes, congrès.
          Une marque s'écrit <b>une seule fois ici</b> et sert à toutes les entreprises qui la taguent — corriger
          un pseudo ici le corrige partout. Ce qui est propre à une entreprise, comme son commercial de secteur,
          s'ajoute sur la page de son module Social Media.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)} aria-label="Rechercher une marque"
            placeholder="Rechercher une marque ou un compte…" data-testid="entite-recherche" className="inp max-w-md" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={aVerifier} onChange={(e) => setAVerifier(e.target.checked)}
              data-testid="entite-filtre-verifier" className="w-4 h-4 accent-marine" />
            À vérifier seulement
          </label>
          <span className="text-sm text-marine/80">
            {filtrees.length} sur {entites.length}{sansCompte ? ` · ${sansCompte} sans aucun compte` : ""}{nonVerifies ? ` · ${nonVerifies} compte(s) à vérifier` : ""}
          </span>
        </div>
      </Card>

      {creation && (
        <Card title="Nouvelle marque">
          <div className="grid sm:grid-cols-3 gap-3 max-w-2xl">
            <input autoFocus value={creation.name} onChange={(e) => setCreation({ ...creation, name: e.target.value })}
              aria-label="Nom de la marque" placeholder="Ex. Nobel Biocare" data-testid="entite-nom" className="inp" />
            <select value={creation.kind} onChange={(e) => setCreation({ ...creation, kind: e.target.value })}
              aria-label="Type" data-testid="entite-type" className="inp">
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <div className="flex gap-2">
              <button onClick={creer} data-testid="entite-creer" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover">Ajouter</button>
              <button onClick={() => setCreation(null)} className="text-sm text-marine/80 px-2">Annuler</button>
            </div>
          </div>
          <p className="text-xs text-marine/80 mt-2">Ses comptes s'ajoutent ensuite, en dépliant sa ligne. Une marque sans compte est utile : elle dit ce qui reste à chercher.</p>
        </Card>
      )}

      <Card>
        <div className="space-y-2" data-testid="entites-liste">
          {filtrees.length === 0 && <p className="text-sm text-marine/80">Aucune marque ne correspond.</p>}
          {filtrees.map((e) => {
            const deplie = ouvert === e.id;
            const comptes = e.accounts || [];
            return (
              <div key={e.id} data-testid={`entite-${e.id}`} className="rounded-sm border border-marine/10 p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={() => setOuvert(deplie ? null : e.id)} data-testid={`entite-ouvrir-${e.id}`}
                    className="font-medium text-marine flex items-center gap-1.5 hover:opacity-70">
                    <Icons.ChevronRight size={16} className={`text-marine transition-transform ${deplie ? "rotate-90" : ""}`} />
                    {e.name}
                  </button>
                  <span className="text-xs px-2 py-0.5 rounded-sm bg-sable border border-marine/20 text-marine/80">{e.kind}</span>
                  <span className="text-xs text-marine/80">
                    {comptes.length ? `${comptes.length} compte${comptes.length > 1 ? "s" : ""}` : "aucun compte"}
                    {e.use_count ? ` · employé dans ${e.use_count} liste${e.use_count > 1 ? "s" : ""}` : ""}
                  </span>
                  <button aria-label={`Retirer ${e.name} de l'annuaire`} onClick={() => supprimer(e)} data-testid={`entite-supprimer-${e.id}`}
                    className="text-rouge-texte ml-auto shrink-0"><Icons.Trash2 size={16} /></button>
                </div>

                {comptes.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 pl-6">
                    {comptes.map((c, i) => (
                      // `max-w-full` : sans lui la pastille déborde de sa carte à 320 px (jusqu'à 47 px
                      // mesurés sur « @vita_zahnfabrik Instagram à vérifier ») — la page ne défile pas pour
                      // autant, le défaut ne se voit donc qu'en comparant l'enfant au cadre de sa carte.
                      // C'est le pseudo qui se tronque : le réseau et « à vérifier » gardent leur place.
                      <span key={c.id || i} title={c.verified ? "Vérifié" : "À vérifier : l'existence du compte n'a pas été confirmée"}
                        className="inline-flex max-w-full items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-sable border border-marine/20 text-marine">
                        <span className="truncate min-w-0">{c.handle}</span>
                        <span className="text-marine/80 shrink-0">{c.network}</span>
                        {!c.verified && <span className="shrink-0 px-1.5 rounded-sm bg-ambre-50 text-ambre-texte border border-ambre/40">à vérifier</span>}
                        {deplie && (
                          <button aria-label={c.verified ? `Marquer ${c.handle} à vérifier` : `Marquer ${c.handle} vérifié`}
                            onClick={() => basculerVerifie(e, i)} data-testid={`entite-compte-verifie-${e.id}-${i}`}
                            className={c.verified ? "text-bleu-fonce" : "text-marine/80"}>
                            <Icons.CheckCircle2 size={16} />
                          </button>
                        )}
                        {deplie && <button aria-label={`Retirer ${c.handle}`} onClick={() => retirerCompte(e, i)} data-testid={`entite-compte-retirer-${e.id}-${i}`}
                          className="text-rouge-texte"><Icons.X size={16} /></button>}
                      </span>
                    ))}
                  </div>
                )}

                {deplie && (
                  <div className="mt-3 pl-6 pt-3 border-t border-marine/10 space-y-3">
                    <div className="grid sm:grid-cols-4 gap-2 max-w-3xl">
                      <input aria-label="Nom du compte" placeholder="Nom (facultatif)" value={compte.name}
                        onChange={(ev) => setCompte({ ...compte, name: ev.target.value })} data-testid={`entite-compte-nom-${e.id}`} className="inp" />
                      <input aria-label="Identifiant du compte" placeholder="@compte ou lien" value={compte.handle}
                        onChange={(ev) => setCompte({ ...compte, handle: ev.target.value })} data-testid={`entite-compte-handle-${e.id}`} className="inp" />
                      <select aria-label="Réseau" value={compte.network} onChange={(ev) => setCompte({ ...compte, network: ev.target.value })}
                        data-testid={`entite-compte-reseau-${e.id}`} className="inp">
                        {Object.keys(POST_PLATFORMS).map((r) => <option key={r} value={r}>{POST_PLATFORMS[r].label}</option>)}
                      </select>
                      <button onClick={() => ajouterCompte(e)} data-testid={`entite-compte-ajouter-${e.id}`}
                        className="bg-marine text-white rounded-sm text-sm px-4 hover:bg-marine-hover">Ajouter</button>
                    </div>
                    <label className="text-sm block max-w-2xl">Ce qui manque
                      <input defaultValue={e.missing || ""} placeholder="Ex. les autres réseaux, jamais relevés"
                        onBlur={(ev) => ev.target.value !== (e.missing || "") && ecrire(e, { missing: ev.target.value }, "Noté")}
                        data-testid={`entite-manque-${e.id}`} className="inp mt-1" />
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
